import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from './api'
import { useAuth } from './useAuth'

// Fixados não têm teto — o usuário decide quantos atalhos quer. Recentes continua em 3 porque
// é uma lista automática, não uma escolha dele.
export const MAX_RECENT_ROUTES = 3

// Recentes moram no navegador, não no banco: é histórico de navegação daquela máquina, some quando
// o usuário limpa o storage e não precisa acompanhá-lo entre dispositivos. A chave leva o id do
// usuário porque a mesma máquina é usada por mais de um — sem isso um veria os recentes do outro.
const RECENT_STORAGE_PREFIX = '@BFCDigital:menu-recent'

function recentStorageKey(userId: number): string {
  return `${RECENT_STORAGE_PREFIX}:${userId}`
}

function readStoredRecent(userId: number): string[] {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const raw = window.localStorage.getItem(recentStorageKey(userId))

    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw)

    if (!Array.isArray(parsed)) {
      return []
    }

    // Storage é editável pelo usuário e sobrevive a mudanças de versão: só entra o que ainda tem
    // a forma esperada, senão o menu quebraria ao renderizar lixo salvo por uma versão antiga.
    return parsed
      .filter((item): item is string => typeof item === 'string' && !!item)
      .slice(0, MAX_RECENT_ROUTES)
  } catch {
    return []
  }
}

function writeStoredRecent(userId: number, routes: string[]): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(
      recentStorageKey(userId),
      JSON.stringify(routes)
    )
  } catch {
    // Storage cheio ou bloqueado (aba anônima, política do navegador): a lista continua valendo
    // em memória para esta sessão, só não sobrevive ao reload.
  }
}

interface IMenuPreferencesResponse {
  pinned_routes: string[]
  is_collapsed: boolean | null
  is_recent_collapsed: boolean
}

interface IMenuPreferences {
  pinned: string[]
  recent: string[]
  isPinned: (route: string) => boolean
  /** Fixa ou desafixa. */
  togglePin: (route: string) => void
  /** Muda um fixado de posição — a ordem da lista é a que aparece no menu. */
  reorderPinned: (from: number, to: number) => void
  registerVisit: (route: string) => void
  /**
   * Menu recolhido segundo o servidor. `null` enquanto a resposta não chegou ou para quem nunca
   * escolheu — nos dois casos vale o padrão da tela.
   */
  collapsed: boolean | null
  setCollapsed: (value: boolean) => void
  /** Seção RECENTES recolhida no menu expandido. */
  recentCollapsed: boolean
  setRecentCollapsed: (value: boolean) => void
}

/**
 * Itens fixados e telas recentes do menu lateral.
 *
 * Os fixados (e o estado recolhido do menu) são preferência declarada do usuário e ficam no
 * servidor, em `users/me/menu-preferences`, para acompanhá-lo em qualquer máquina. Já os
 * recentes ficam apenas no `localStorage` do navegador: é rastro de navegação, gerado sozinho a
 * cada tela aberta, e gravá-lo no banco custaria um PUT por navegação sem nada em troca.
 *
 * As duas listas guardam rotas, não títulos: o rótulo sai do menu já filtrado por permissão na
 * hora de renderizar, então um item que o usuário deixou de poder ver simplesmente não aparece,
 * sem precisar de limpeza no banco.
 *
 * A gravação é otimista — a lista muda na tela na hora e o PUT vai atrás. Preferência de
 * navegação não é dado crítico: se a requisição falhar, o menu continua utilizável e o estado se
 * corrige no próximo carregamento.
 */
export function useMenuPreferences(): IMenuPreferences {
  const { user } = useAuth()

  const userId = user?.id

  const [pinned, setPinned] = useState<string[]>([])
  const [recent, setRecent] = useState<string[]>([])
  const [collapsed, setCollapsedState] = useState<boolean | null>(null)
  const [recentCollapsed, setRecentCollapsedState] = useState(false)

  // As listas também vivem em refs porque dois cliques no mesmo tick leriam o mesmo estado
  // defasado — o segundo desfaria o primeiro.
  const pinnedRef = useRef<string[]>([])
  const recentRef = useRef<string[]>([])

  // Só grava depois de ter lido o que já estava salvo, senão o primeiro registro de visita
  // sobrescreveria os fixados do usuário com uma lista vazia.
  const loadedRef = useRef(false)
  const pendingVisitRef = useRef<string | null>(null)

  const applyPinned = useCallback((next: string[]) => {
    pinnedRef.current = next
    setPinned(next)
  }, [])

  const applyRecent = useCallback(
    (next: string[], persistToStorage = true) => {
      recentRef.current = next
      setRecent(next)

      if (persistToStorage && userId) {
        writeStoredRecent(userId, next)
      }
    },
    [userId]
  )

  const persist = useCallback((payload: Partial<IMenuPreferencesResponse>) => {
    if (!loadedRef.current) {
      return
    }

    api.put('users/me/menu-preferences', payload).catch(() => {
      //
    })
  }, [])

  const applyVisit = useCallback(
    (route: string) => {
      // Fixado não entra em recentes — já está no topo do menu.
      if (pinnedRef.current.includes(route)) {
        return
      }

      const current = recentRef.current

      if (current[0] === route) {
        return
      }

      const next = [route, ...current.filter(item => item !== route)].slice(
        0,
        MAX_RECENT_ROUTES
      )

      applyRecent(next)
    },
    [applyRecent]
  )

  useEffect(() => {
    if (!userId) {
      return
    }

    let active = true

    // Leitura síncrona: o storage já está aqui, não faz sentido a seção RECENTES aparecer vazia
    // esperando a resposta do servidor, que só traz os fixados.
    applyRecent(readStoredRecent(userId), false)

    function finishLoad() {
      loadedRef.current = true

      // A tela por onde o usuário entrou é visitada antes da resposta chegar: sem isso ela só
      // entraria em RECENTES na próxima vez que ele a abrisse.
      const pending = pendingVisitRef.current
      pendingVisitRef.current = null

      if (pending) {
        applyVisit(pending)
      }
    }

    api
      .get<IMenuPreferencesResponse>('users/me/menu-preferences')
      .then(({ data }) => {
        if (!active) {
          return
        }

        applyPinned(data?.pinned_routes || [])
        setCollapsedState(data?.is_collapsed ?? null)
        setRecentCollapsedState(Boolean(data?.is_recent_collapsed))
        finishLoad()
      })
      .catch(() => {
        if (!active) {
          return
        }

        // Sem resposta o menu abre sem fixados, que é o mesmo estado de quem nunca fixou nada.
        finishLoad()
      })

    return () => {
      active = false
    }
  }, [userId, applyPinned, applyRecent, applyVisit])

  const isPinned = useCallback(
    (route: string) => pinnedRef.current.includes(route),
    []
  )

  const togglePin = useCallback(
    (route: string) => {
      if (!route) {
        return
      }

      const current = pinnedRef.current

      if (current.includes(route)) {
        const next = current.filter(item => item !== route)

        applyPinned(next)
        persist({ pinned_routes: next })
        return
      }

      const next = [...current, route]
      // Item fixado sai dos recentes: as duas seções nunca repetem a mesma tela.
      const nextRecent = recentRef.current.filter(item => item !== route)

      applyPinned(next)
      applyRecent(nextRecent)
      persist({ pinned_routes: next })
    },
    [applyPinned, applyRecent, persist]
  )

  const reorderPinned = useCallback(
    (from: number, to: number) => {
      const current = pinnedRef.current

      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= current.length ||
        to >= current.length
      ) {
        return
      }

      const next = [...current]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)

      applyPinned(next)
      persist({ pinned_routes: next })
    },
    [applyPinned, persist]
  )

  const setCollapsed = useCallback(
    (value: boolean) => {
      setCollapsedState(value)
      persist({ is_collapsed: value })
    },
    [persist]
  )

  const setRecentCollapsed = useCallback(
    (value: boolean) => {
      setRecentCollapsedState(value)
      persist({ is_recent_collapsed: value })
    },
    [persist]
  )

  const registerVisit = useCallback(
    (route: string) => {
      if (!route) {
        return
      }

      if (!loadedRef.current) {
        pendingVisitRef.current = route
        return
      }

      applyVisit(route)
    },
    [applyVisit]
  )

  return {
    pinned,
    recent,
    isPinned,
    togglePin,
    reorderPinned,
    registerVisit,
    collapsed,
    setCollapsed,
    recentCollapsed,
    setRecentCollapsed
  }
}
